/**
 * Formatting only. Nothing here does arithmetic on money.
 *
 * Every GBP figure arrives as a string because it is a Decimal carrying fractions of a penny, and
 * the moment the portal parses one into a JavaScript number to add it up, the total it shows stops
 * matching the total the API computed. So these functions round for display and hand back a string,
 * and any figure the portal needs is asked of the API rather than derived here.
 */

const gbp = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function money(value: string | null | undefined, absent = '—'): string {
  if (value === null || value === undefined) return absent;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? gbp.format(parsed) : absent;
}

const count = new Intl.NumberFormat('en-GB');

export function num(value: number | null | undefined, absent = '—'): string {
  return value === null || value === undefined ? absent : count.format(value);
}

/** Milliseconds as something a person reads: 850 ms, 3.2 s, 1m 12s. */
export function duration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const minutes = Math.floor(ms / 60_000);
  return `${minutes}m ${Math.round((ms % 60_000) / 1000)}s`;
}

/** A share as a whole percentage, with the zero-of-zero case read as nothing rather than NaN. */
export function percent(part: number, whole: number): string {
  if (!whole) return '—';
  return `${Math.round((part / whole) * 100)}%`;
}

const dateOnly = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const dateAndTime = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const timeOnly = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });

export function day(value: string | null | undefined): string {
  return value ? dateOnly.format(new Date(value)) : '—';
}

export function moment(value: string | null | undefined): string {
  return value ? dateAndTime.format(new Date(value)) : '—';
}

export function clock(value: string | null | undefined): string {
  return value ? timeOnly.format(new Date(value)) : '—';
}

export function monthName(value: string): string {
  return new Date(value).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

/**
 * "3 days ago", or "Never".
 *
 * Never is deliberately not "a long time ago". A customer created last week who has not opened the
 * app once has a different problem from one who used it daily and stopped, and the list is read to
 * tell those two apart.
 */
export function since(value: string | null | undefined): string {
  if (!value) return 'Never';
  const then = new Date(value).getTime();
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days < 31) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  return dateOnly.format(new Date(value));
}

/** A label for how somebody spoke to Electra, in the words the screens use. */
export function modeLabel(mode: string | null | undefined): string {
  switch (mode) {
    case 'voice':
      return 'Voice';
    case 'night_shift':
      return 'Night shift';
    case 'chat':
      return 'Text';
    default:
      return '—';
  }
}
